import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AutoCompleteMultiSelectBox,
  Option,
  RenderOptionContentArgs,
} from './AutoCompleteMultiSelectBox';
import { Badge } from './Badge';
import {
  isEncodedTagValue,
  decodeTagValue,
  buildEncodedTagOptions,
} from './tag/tag.helpers';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Number, NumberTags } from 'src/interfaces/number.interface';
import { processPhoneNumber } from 'src/utils/number';

const MAX_OPTIONS = 10;

type AutoCompleteMultiSelectAgentNumberProps = {
  selectedOptions: Option[];
  onSelectionChange: (options: Option[]) => void;
  className?: string;
  disabled?: boolean;
  direction?: 'up' | 'down';
  placeholder?: string;
  noOptionsText?: string;
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
};

export const AutoCompleteMultiSelectAgentNumber = ({
  selectedOptions,
  onSelectionChange,
  className,
  disabled,
  direction,
  placeholder = 'Select numbers or tags...',
  noOptionsText = 'No matching numbers or tags found',
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
}: AutoCompleteMultiSelectAgentNumberProps) => {
  const { get } = useAuthedRequest();
  const [options, setOptions] = useState<Option[]>(selectedOptions);

  const mergeWithSelectedOptions = useCallback(
    (nextOptions: Option[]) => {
      const optionValues = new Set(nextOptions.map((option) => option.value));
      const missingSelections = selectedOptions.filter(
        (option) => !optionValues.has(option.value),
      );
      return [...nextOptions, ...missingSelections];
    },
    [selectedOptions],
  );

  useEffect(() => {
    setOptions((prev) => mergeWithSelectedOptions(prev));
  }, [mergeWithSelectedOptions, selectedOptions]);

  const fetchNumberTagOptions = useCallback(
    async (query: string): Promise<Option[]> => {
      try {
        const queryObject: QueryObject = {
          page: '1',
          size: '10',
        };
        const trimmedQuery = query.trim();
        if (trimmedQuery) {
          queryObject.filters = JSON.stringify({ startsWith: trimmedQuery });
        }
        const params = new URLSearchParams(
          queryObject as Record<string, string>,
        );
        const data: NumberTags = await get(
          `/numbers/tags?${params.toString()}`,
        );
        const tags = data.items || [];
        return buildEncodedTagOptions(tags);
      } catch (error) {
        console.error('Failed to fetch number tags:', error);
        return [];
      }
    },
    [get],
  );

  const fetchNumberOptions = useCallback(
    async (query: string): Promise<Option[]> => {
      try {
        const queryObject: QueryObject = {
          page: '1',
          size: '10',
        };
        const trimmedQuery = query.trim();
        if (trimmedQuery) {
          queryObject.filters = JSON.stringify({ number: trimmedQuery });
        }
        const params = new URLSearchParams(
          queryObject as Record<string, string>,
        );
        const data = await get(`/numbers?${params.toString()}`);
        const numbers = (data.items || []) as Number[];
        return numbers.map((record) => ({
          value: record.number,
          label: processPhoneNumber(record.number),
        }));
      } catch (error) {
        console.error('Failed to fetch numbers:', error);
        return [];
      }
    },
    [get],
  );

  const fetchOptions = useCallback(
    async (query: string) => {
      try {
        const [tagOptions, numberOptions] = await Promise.all([
          fetchNumberTagOptions(query),
          fetchNumberOptions(query),
        ]);
        const mergedOptions = [...tagOptions, ...numberOptions].slice(
          0,
          MAX_OPTIONS,
        );
        setOptions(mergeWithSelectedOptions(mergedOptions));
      } catch (error) {
        console.error('Failed to fetch agent-number options:', error);
      }
    },
    [fetchNumberOptions, fetchNumberTagOptions, mergeWithSelectedOptions],
  );

  const renderOptionContent = useCallback(
    ({ option }: RenderOptionContentArgs) => {
      if (!isEncodedTagValue(option.value)) {
        return option.label || option.value;
      }

      const label = option.label || decodeTagValue(option.value);

      return (
        <Badge variant="primary" className="px-2 py-1 text-xs font-medium">
          {label}
        </Badge>
      );
    },
    [],
  );

  const boxProps = useMemo(
    () => ({
      className,
      disabled,
      direction,
      placeholder,
      noOptionsText,
      variant,
      color,
      size,
    }),
    [
      className,
      color,
      direction,
      disabled,
      noOptionsText,
      placeholder,
      size,
      variant,
    ],
  );

  return (
    <AutoCompleteMultiSelectBox
      {...boxProps}
      options={options}
      fetchOptions={fetchOptions}
      selectedOptions={selectedOptions}
      onSelectionChange={onSelectionChange}
      renderOptionContent={renderOptionContent}
    />
  );
};
